import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  ArrowLeft,
  Edit,
  Loader2,
  Factory,
  Package,
  Play,
  CheckCircle,
  XCircle,
  AlertCircle,
  FileText,
  Truck,
  Activity,
  History,
  RefreshCw,
  Link,
  Trash2,
  Plus,
  Printer,
  Save,
  Search,
  Download,
  Layers,
  ExternalLink,
  PackagePlus,
} from 'lucide-react';
import Layout from '../components/Layout';
import authService from '../services/authService';
import productionService from '../services/productionService';
import materialRequisitionService from '../services/materialRequisitionService';
import warehouseService from '../services/warehouseService';
import bomService from '../services/bomService';
import scheduleService from '../services/scheduleService';
import CompleteWorkOrderDialog from '../components/workOrder/CompleteWorkOrderDialog';
import MaterialIssueDialog from '../components/workOrder/MaterialIssueDialog';
import ProductionScheduleStepTracker from '../components/production/ProductionScheduleStepTracker';
import CreateSubcontractDialog from '../components/subcontract/CreateSubcontractDialog';
// eslint-disable-next-line no-unused-vars
import BomForm from '../components/BomForm';
import BomCreationDrawer from '../components/BomCreationDrawer';
import FileAttachmentManager from '../components/FileAttachmentManager';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { getProductionStatusLabel } from '../constants/productionOrderStatuses';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import SortableTableRow from '../components/SortableTableRow';
import productService from '../services/productService';
import { Input } from '../components/ui/input';
import * as XLSX from 'xlsx';

function ProductionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errorType, setErrorType] = useState(null);
  const [order, setOrder] = useState(null);
  const [scheduleProgress, setScheduleProgress] = useState(null);

  // Dialog states
  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [materialRequisitions, setMaterialRequisitions] = useState([]);
  
  // BOM Assignment states
  const [assignBomDialogOpen, setAssignBomDialogOpen] = useState(false);
  const [selectedItemIndex, setSelectedItemIndex] = useState(null);
  const [availableBoms, setAvailableBoms] = useState([]);
  const [selectedBomId, setSelectedBomId] = useState('');
  const [bomLoading, setBomLoading] = useState(false);
  
  // BOM Creation Sheet states
  const [bomSheetOpen, setBomSheetOpen] = useState(false);
  const [bomTargetProduct, setBomTargetProduct] = useState(null);

  // BOM Detail Drawer states
  const [bomDetailOpen, setBomDetailOpen] = useState(false);
  const [bomDetailData, setBomDetailData] = useState(null);
  const [bomDetailLoading, setBomDetailLoading] = useState(false);
  const [bomDetailError, setBomDetailError] = useState(null);

  // Product Detail Drawer states
  const [productDrawerOpen, setProductDrawerOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productLoading, setProductLoading] = useState(false);
  const [productError, setProductError] = useState(null);

  // Material Issue History states
  const [linkedMovements, setLinkedMovements] = useState([]);

  const [printDialogOpen, setPrintDialogOpen] = useState(false);

  const [isEditing, setIsEditing] = useState(false);
  const [editItems, setEditItems] = useState([]);
  const [editProductionOrderId, setEditProductionOrderId] = useState('');
  const [editActualStartDate, setEditActualStartDate] = useState('');
  const [editActualEndDate, setEditActualEndDate] = useState('');
  const [editPlannedStartDate, setEditPlannedStartDate] = useState('');
  const [editPlannedEndDate, setEditPlannedEndDate] = useState('');
  const [editPriority, setEditPriority] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [productSearchOpen, setProductSearchOpen] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState('');

  // Subcontract dialog states
  const [subcontractDialogOpen, setSubcontractDialogOpen] = useState(false);
  const [productSearchResults, setProductSearchResults] = useState([]);
  const [productSearchLoading, setProductSearchLoading] = useState(false);

  // Record additional leftover states
  const [recordLeftoverDialogOpen, setRecordLeftoverDialogOpen] = useState(false);
  const [recordLeftoverLoading, setRecordLeftoverLoading] = useState(false);
  const [recordLeftoverBars, setRecordLeftoverBars] = useState([]);
  const [recordLeftoverJustification, setRecordLeftoverJustification] = useState('');
  const [showAddLeftoverForm, setShowAddLeftoverForm] = useState(false);
  const [newLeftoverBar, setNewLeftoverBar] = useState({
    material_code: '',
    material_name: '',
    length: '',
    quantity: 1,
    location: 'main',
    product_id: '',
    leftover_type: 'blank',
    tip_diameter: ''
  });

  // Additional receipt states (追加入库)
  const [additionalReceiptDialogOpen, setAdditionalReceiptDialogOpen] = useState(false);
  const [additionalReceiptLoading, setAdditionalReceiptLoading] = useState(false);
  const [additionalReceiptItems, setAdditionalReceiptItems] = useState([]);
  const [additionalReceiptLocation, setAdditionalReceiptLocation] = useState('main');
  const [additionalReceiptNotes, setAdditionalReceiptNotes] = useState('');

  // Partial leftover receipt states (余料部分入库)
  const [partialLeftoverDialogOpen, setPartialLeftoverDialogOpen] = useState(false);
  const [partialLeftoverLoading, setPartialLeftoverLoading] = useState(false);
  const [partialLeftoverBars, setPartialLeftoverBars] = useState([]);
  const [partialLeftoverNotes, setPartialLeftoverNotes] = useState('');

  // Load user info
  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    setUser(currentUser);
  }, []);

  // Load order data
  const loadOrder = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setErrorType(null);

      const orderData = await productionService.getProductionOrder(id);
      setOrder(orderData);

      // Load schedule-based progress (source of truth from schedule board)
      try {
        const progressData = await scheduleService.getOrderScheduleProgress(id);
        setScheduleProgress(progressData);
      } catch (err) {
        console.error('Failed to load schedule progress:', err);
        setScheduleProgress(null);
      }

      // Load material requisitions
      try {
        const reqResult = await materialRequisitionService.getRequisitionsByProductionOrder(id);
        setMaterialRequisitions(reqResult.requisitions || reqResult.items || []);
      } catch (err) {
        console.error('Failed to load material requisitions:', err);
        setMaterialRequisitions([]);
      }

      // Load material issues for history display
      try {
        const movementData = orderData.material_issue_movements || [];
        
        // Backend _serialize_order already replaces IDs with full movement objects
        const isAlreadyFullObjects = Array.isArray(movementData) && movementData.length > 0 && 
          typeof movementData[0] === 'object' && movementData[0].movement_id;
        
        if (isAlreadyFullObjects) {
          setLinkedMovements(movementData);
        } else if (Array.isArray(movementData) && movementData.length > 0) {
          const movements = await warehouseService.getMovementsByIds(movementData);
          setLinkedMovements(movements);
        } else {
          setLinkedMovements([]);
        }
      } catch (err) {
        console.error('Failed to load material issues:', err);
        setLinkedMovements([]);
      }

    } catch (err) {
      console.error('Failed to load production order:', err);
      setError('Failed to load production order details. Please try again.');
      setErrorType('destructive');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  const handleBack = () => {
    navigate('/production');
  };

  const handleExportToExcel = () => {
    if (!order) return;

    const formatDate = (dateValue) => {
      if (!dateValue) return '-';
      if (typeof dateValue === 'string') return new Date(dateValue).toLocaleDateString('zh-CN');
      if (dateValue.$date) return new Date(dateValue.$date).toLocaleDateString('zh-CN');
      return new Date(dateValue).toLocaleDateString('zh-CN');
    };

    const statusLabelMap = {
      draft: '草稿',
      released: '已发布',
      in_progress: '生产中',
      completed: '已完成',
      cancelled: '已取消',
    };

    const priorityLabelMap = {
      low: '低',
      normal: '普通',
      high: '高',
      urgent: '紧急',
    };

    // Sheet 1: 工单概览
    const overviewData = [
      ['工单号', order.production_order_id || '-'],
      ['状态', statusLabelMap[order.status] || order.status || '-'],
      ['优先级', priorityLabelMap[order.priority] || order.priority || '-'],
      ['客户', order.client_name || '-'],
      ['计划开始时间', formatDate(order.planned_start_date)],
      ['计划结束时间', formatDate(order.planned_end_date)],
      ['实际开始时间', formatDate(order.actual_start_date)],
      ['实际结束时间', formatDate(order.actual_end_date)],
      ['关联销售订单', order.source_reference || '-'],
      ['备注', order.notes || '-'],
    ];

    const overviewSheet = XLSX.utils.aoa_to_sheet(overviewData);
    overviewSheet['!cols'] = [
      { wch: 16 },
      { wch: 40 },
    ];

    // Sheet 2: 产品清单
    const itemsHeaders = ['序号', '产品代码', '产品名称', '规格', '数量', '备注'];
    const itemsData = [itemsHeaders];

    (order.items || []).forEach((item, index) => {
      itemsData.push([
        index + 1,
        item.finished_product_code || '-',
        item.finished_product_name || '-',
        item.product_details?.specification || item.specification || '-',
        item.planned_quantity || 0,
        '',
      ]);
    });

    const itemsSheet = XLSX.utils.aoa_to_sheet(itemsData);
    itemsSheet['!cols'] = [
      { wch: 8 },
      { wch: 16 },
      { wch: 28 },
      { wch: 24 },
      { wch: 10 },
      { wch: 20 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, overviewSheet, '工单概览');
    XLSX.utils.book_append_sheet(wb, itemsSheet, '产品清单');

    const dateStr = new Date().toISOString().split('T')[0];
    const filename = `${order.production_order_id || '工单'}_导出_${dateStr}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  const handleStartEdit = () => {
    setEditItems(order?.items?.map((item, index) => ({
      ...item,
      _editId: `edit-${index}-${Date.now()}`,
      finished_product_id: item.finished_product_id?.$oid || item.finished_product_id?._id || item.finished_product_id || '',
      bom_id: item.bom_id?.$oid || item.bom_id?._id || item.bom_id || '',
    })) || []);
    setEditProductionOrderId(order?.production_order_id || '');
    setEditActualStartDate(order?.actual_start_date ? order.actual_start_date.split('T')[0] : '');
    setEditActualEndDate(order?.actual_end_date ? order.actual_end_date.split('T')[0] : '');
    setEditPlannedStartDate(order?.planned_start_date ? order.planned_start_date.split('T')[0] : '');
    setEditPlannedEndDate(order?.planned_end_date ? order.planned_end_date.split('T')[0] : '');
    setEditPriority(order?.priority || 'normal');
    setIsEditing(true);
    setError(null);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditItems([]);
    setEditProductionOrderId('');
    setEditActualStartDate('');
    setEditActualEndDate('');
    setEditPlannedStartDate('');
    setEditPlannedEndDate('');
    setEditPriority('');
    setError(null);
  };

  const handleSaveEdit = async () => {
    if (editItems.length === 0) {
      setError('至少需要一个产品项目');
      setErrorType('destructive');
      return;
    }

    const invalidItem = editItems.find(item => !item.planned_quantity || item.planned_quantity <= 0);
    if (invalidItem) {
      setError('所有项目的计划数量必须大于0');
      setErrorType('destructive');
      return;
    }

    try {
      setEditSaving(true);
      setError(null);

      const itemsPayload = editItems.map((item) => ({
        finished_product_id: typeof item.finished_product_id === 'object' 
          ? (item.finished_product_id?.$oid || item.finished_product_id?._id || String(item.finished_product_id))
          : String(item.finished_product_id),
        finished_product_code: item.finished_product_code || '',
        finished_product_name: item.finished_product_name || '',
        planned_quantity: Number(item.planned_quantity),
        bom_id: item.bom_id ? (typeof item.bom_id === 'object' 
          ? (item.bom_id?.$oid || item.bom_id?._id || String(item.bom_id))
          : String(item.bom_id)) : null,
        item_id: item.item_id || undefined,
        specification: item.product_details?.specification || item.specification || '',
      }));

      await productionService.updateOrderItems(id, itemsPayload);

      const orderUpdates = {};
      if (editProductionOrderId && editProductionOrderId !== order?.production_order_id) {
        orderUpdates.production_order_id = editProductionOrderId;
      }
      if (editActualStartDate !== (order?.actual_start_date ? order.actual_start_date.split('T')[0] : '')) {
        orderUpdates.actual_start_date = editActualStartDate || null;
      }
      if (editActualEndDate !== (order?.actual_end_date ? order.actual_end_date.split('T')[0] : '')) {
        orderUpdates.actual_end_date = editActualEndDate || null;
      }
      if (editPlannedStartDate !== (order?.planned_start_date ? order.planned_start_date.split('T')[0] : '')) {
        orderUpdates.planned_start_date = editPlannedStartDate || null;
      }
      if (editPlannedEndDate !== (order?.planned_end_date ? order.planned_end_date.split('T')[0] : '')) {
        orderUpdates.planned_end_date = editPlannedEndDate || null;
      }
      if (editPriority !== (order?.priority || 'normal')) {
        orderUpdates.priority = editPriority;
      }
      if (Object.keys(orderUpdates).length > 0) {
        await productionService.updateProductionOrder(id, orderUpdates);
      }

      setIsEditing(false);
      setEditItems([]);
      setEditProductionOrderId('');
      setEditActualStartDate('');
      setEditActualEndDate('');
      setEditPlannedStartDate('');
      setEditPlannedEndDate('');
      setEditPriority('');
      await loadOrder();
    } catch (err) {
      setError(`保存失败: ${err.message}`);
      setErrorType('destructive');
    } finally {
      setEditSaving(false);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setEditItems((prev) => {
        const oldIndex = prev.findIndex((item) => item._editId === active.id);
        const newIndex = prev.findIndex((item) => item._editId === over.id);
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  };

  const handleEditQuantityChange = (editId, value) => {
    setEditItems((prev) =>
      prev.map((item) =>
        item._editId === editId ? { ...item, planned_quantity: Number(value) || 0 } : item
      )
    );
  };

  const handleRemoveEditItem = (editId) => {
    if (!window.confirm('确定要删除此产品吗？')) return;
    setEditItems((prev) => prev.filter((item) => item._editId !== editId));
  };

  const searchProducts = useCallback(async () => {
    try {
      setProductSearchLoading(true);
      const response = await productService.searchProducts(productSearchTerm, 1, 20);
      setProductSearchResults(response.products || []);
    } catch (err) {
      console.error('Failed to search products:', err);
    } finally {
      setProductSearchLoading(false);
    }
  }, [productSearchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (productSearchOpen) searchProducts();
    }, 300);
    return () => clearTimeout(timer);
  }, [productSearchTerm, productSearchOpen, searchProducts]);

  const handleSelectProduct = async (product) => {
    let bomId = null;
    if (product.default_bom_id) {
      try {
        const bom = await bomService.getBom(product.default_bom_id);
        if (bom && bom.status === 'active') {
          bomId = bom._id;
        } else if (bom) {
          // Default BOM is not active, try to find active one
          console.warn(`Default BOM ${product.default_bom_id} is ${bom.status}, searching for active BOM...`);
          const activeBoms = await bomService.getBomsByProduct(product._id);
          const defaultActiveBom = activeBoms.find(b => b.is_default) || activeBoms[0];
          if (defaultActiveBom) {
            bomId = defaultActiveBom._id;
          }
        }
      } catch (err) {
        console.error('Failed to load default BOM:', err);
      }
    }

    setEditItems((prev) => [
      ...prev,
      {
        _editId: `edit-new-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        item_id: null,
        bom_id: bomId || '',
        bom_code: bomId ? product.product_id : null,
        finished_product_id: product._id,
        finished_product_code: product.product_id,
        finished_product_name: product.name,
        planned_quantity: 1,
        completed_quantity: 0,
        scrapped_quantity: 0,
        product_details: { specification: product.specification || '' },
        material_requirements: [],
        material_info: {},
        process_steps: [],
        status: 'pending',
      },
    ]);
    setProductSearchOpen(false);
    setProductSearchTerm('');
  };

  const handleRelease = async () => {
    try {
      setActionLoading(true);
      await productionService.releaseOrder(id);
      await loadOrder();
    } catch (err) {
      setError(`Failed to release order: ${err.message}`);
      setErrorType('destructive');
    } finally {
      setActionLoading(false);
    }
  };

  const handleStartProduction = async () => {
    try {
      setActionLoading(true);
      await productionService.startProduction(id);
      await loadOrder();
    } catch (err) {
      setError(`Failed to start production: ${err.message}`);
      setErrorType('destructive');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCompleteSuccess = useCallback(async () => {
    await loadOrder();
  }, [loadOrder]);

  const handleCancel = async () => {
    if (!window.confirm('确定要取消此生产工单吗？')) {
      return;
    }

    try {
      setActionLoading(true);
      await productionService.cancelOrder(id);
      await loadOrder();
    } catch (err) {
      setError(`Failed to cancel order: ${err.message}`);
      setErrorType('destructive');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRefreshProcessSteps = async () => {
    if (!window.confirm('确定要刷新工序步骤吗？这将从当前BOM模板重新生成工序步骤。')) {
      return;
    }

    try {
      setActionLoading(true);
      await productionService.refreshProcessSteps(id);
      await loadOrder();
      setError('工序步骤已刷新');
    } catch (err) {
      setError(`Failed to refresh process steps: ${err.message}`);
      setErrorType('destructive');
    } finally {
      setActionLoading(false);
    }
  };

  // Record additional leftover handlers
  const handleOpenRecordLeftoverDialog = () => {
    setRecordLeftoverBars([]);
    setRecordLeftoverJustification('');
    setShowAddLeftoverForm(false);
    setNewLeftoverBar({
      material_code: '',
      material_name: '',
      length: '',
      quantity: 1,
      location: 'main',
      product_id: '',
      leftover_type: 'blank',
      tip_diameter: ''
    });
    setRecordLeftoverDialogOpen(true);
    setError(null);
  };

  const handleCloseRecordLeftoverDialog = () => {
    setRecordLeftoverDialogOpen(false);
    setRecordLeftoverBars([]);
    setRecordLeftoverJustification('');
    setShowAddLeftoverForm(false);
    setError(null);
  };

  // Additional receipt handlers (追加入库)
  const handleOpenAdditionalReceiptDialog = () => {
    setAdditionalReceiptItems(
      (order?.items || []).map((item, idx) => ({
        item_index: idx,
        finished_product_code: item.finished_product_code || '',
        finished_product_name: item.finished_product_name || '',
        specification: item.product_details?.specification || item.specification || '',
        planned_quantity: item.planned_quantity || 0,
        completed_quantity: item.completed_quantity || 0,
        quantity: '',
        use_custom_location: false,
        location: 'main'
      }))
    );
    setAdditionalReceiptLocation('main');
    setAdditionalReceiptNotes('');
    setAdditionalReceiptDialogOpen(true);
    setError(null);
  };

  const handleCloseAdditionalReceiptDialog = () => {
    setAdditionalReceiptDialogOpen(false);
    setAdditionalReceiptItems([]);
    setAdditionalReceiptLocation('main');
    setAdditionalReceiptNotes('');
    setError(null);
  };

  const handleSubmitAdditionalReceipt = async () => {
    const itemsToSubmit = additionalReceiptItems
      .filter(item => parseFloat(item.quantity) > 0)
      .map(item => ({
        item_index: item.item_index,
        quantity: parseFloat(item.quantity),
        location: item.use_custom_location ? item.location : additionalReceiptLocation
      }));

    if (itemsToSubmit.length === 0) {
      setError('请至少为一个产品输入入库数量');
      setErrorType('destructive');
      return;
    }

    try {
      setAdditionalReceiptLoading(true);
      setError(null);
      await productionService.additionalReceipt(
        id,
        itemsToSubmit,
        additionalReceiptLocation,
        additionalReceiptNotes
      );
      await loadOrder();
      handleCloseAdditionalReceiptDialog();
    } catch (err) {
      console.error('Failed to record additional receipt:', err);
      setError(err.response?.data?.error || err.message || '入库失败');
      setErrorType('destructive');
    } finally {
      setAdditionalReceiptLoading(false);
    }
  };

  // Partial leftover receipt handlers (余料部分入库)
  const handleOpenPartialLeftoverDialog = () => {
    setPartialLeftoverBars([]);
    setPartialLeftoverNotes('');
    setShowAddLeftoverForm(false);
    setNewLeftoverBar({
      material_code: '',
      material_name: '',
      length: '',
      quantity: 1,
      location: 'main',
      product_id: '',
      leftover_type: 'blank',
      tip_diameter: ''
    });
    setPartialLeftoverDialogOpen(true);
    setError(null);
  };

  const handleClosePartialLeftoverDialog = () => {
    setPartialLeftoverDialogOpen(false);
    setPartialLeftoverBars([]);
    setPartialLeftoverNotes('');
    setShowAddLeftoverForm(false);
    setError(null);
  };

  const handleAddPartialLeftoverBar = () => {
    if (!newLeftoverBar.material_code) {
      setError('请选择物料类型');
      setErrorType('destructive');
      return;
    }
    if (!newLeftoverBar.length || parseFloat(newLeftoverBar.length) <= 0) {
      setError('请输入有效的余料长度');
      setErrorType('destructive');
      return;
    }

    const availableMaterials = getAvailableMaterialsForLeftover();
    const selectedMaterial = availableMaterials.find(m => m.code === newLeftoverBar.material_code);

    setPartialLeftoverBars(prev => [...prev, {
      id: `manual-${Date.now()}`,
      material_code: newLeftoverBar.material_code,
      material_name: selectedMaterial?.name || '',
      length: parseFloat(newLeftoverBar.length),
      quantity: parseInt(newLeftoverBar.quantity) || 1,
      location: newLeftoverBar.location || 'main',
      product_id: selectedMaterial?.id || '',
      leftover_type: newLeftoverBar.leftover_type || 'blank',
      tip_diameter: newLeftoverBar.leftover_type === 'ground'
        ? (parseFloat(newLeftoverBar.tip_diameter) || 0)
        : undefined
    }]);

    setNewLeftoverBar({
      material_code: '',
      material_name: '',
      length: '',
      quantity: 1,
      location: 'main',
      product_id: '',
      leftover_type: 'blank',
      tip_diameter: ''
    });
    setShowAddLeftoverForm(false);
    setError(null);
  };

  const handleRemovePartialLeftoverBar = (barId) => {
    setPartialLeftoverBars(prev => prev.filter(bar => bar.id !== barId));
  };

  const handleUpdatePartialLeftoverBar = (barId, field, value) => {
    setPartialLeftoverBars(prev => prev.map(bar => {
      if (bar.id !== barId) return bar;
      if (field === 'length') return { ...bar, length: parseFloat(value) || 0 };
      if (field === 'quantity') return { ...bar, quantity: parseInt(value) || 0 };
      if (field === 'location') return { ...bar, location: value };
      return bar;
    }));
  };

  const handleSubmitPartialLeftover = async () => {
    if (partialLeftoverBars.length === 0) {
      setError('请至少添加一条余料记录');
      setErrorType('destructive');
      return;
    }

    try {
      setPartialLeftoverLoading(true);
      setError(null);

      const payload = partialLeftoverBars.map(bar => ({
        material_code: bar.material_code,
        length: bar.length,
        quantity: bar.quantity,
        location: bar.location,
        product_id: bar.product_id,
        leftover_type: bar.leftover_type || 'blank',
        tip_diameter: bar.tip_diameter
      }));

      await productionService.receivePartialLeftover(
        id,
        payload,
        partialLeftoverNotes
      );

      await loadOrder();
      handleClosePartialLeftoverDialog();
    } catch (err) {
      console.error('Failed to receive partial leftover:', err);
      setError(err.response?.data?.error || err.message || '余料部分入库失败');
      setErrorType('destructive');
    } finally {
      setPartialLeftoverLoading(false);
    }
  };

  const getAvailableMaterialsForLeftover = useCallback(() => {
    const materials = new Map();

    // From linked movements
    linkedMovements.forEach(move => {
      move.products?.forEach(prod => {
        const code = prod.product_id || prod.material_product_code || prod.product_code;
        const name = prod.product_name || prod.material_product_name || prod.name || '';
        const pid = prod._id || prod.material_product_id || prod.product_id;
        if (code && !materials.has(code)) {
          materials.set(code, { code, name, id: pid || '' });
        }
      });
    });

    // From order items material requirements
    order?.items?.forEach(item => {
      item.material_requirements?.forEach(mat => {
        const code = mat.material_product_code || mat.material_code;
        const pid = mat.material_product_id || mat._id;
        if (code && !materials.has(code)) {
          materials.set(code, {
            code,
            name: mat.material_name || mat.material_product_name || '',
            id: pid || ''
          });
        }
      });
      // From material_info
      if (item.material_info) {
        const mi = item.material_info;
        const code = mi.product_code || mi.material_code;
        const pid = mi.product_id || mi.material_product_id;
        if (code && !materials.has(code)) {
          materials.set(code, {
            code,
            name: mi.product_name || mi.material_name || '',
            id: pid || ''
          });
        }
      }
    });

    return Array.from(materials.values());
  }, [linkedMovements, order]);

  const handleAddLeftoverBar = () => {
    if (!newLeftoverBar.material_code) {
      setError('请选择物料类型');
      setErrorType('destructive');
      return;
    }
    if (!newLeftoverBar.length || parseFloat(newLeftoverBar.length) <= 0) {
      setError('请输入有效的余料长度');
      setErrorType('destructive');
      return;
    }

    const availableMaterials = getAvailableMaterialsForLeftover();
    const selectedMaterial = availableMaterials.find(m => m.code === newLeftoverBar.material_code);

    setRecordLeftoverBars(prev => [...prev, {
      id: `manual-${Date.now()}`,
      material_code: newLeftoverBar.material_code,
      material_name: selectedMaterial?.name || '',
      length: parseFloat(newLeftoverBar.length),
      quantity: parseInt(newLeftoverBar.quantity) || 1,
      location: newLeftoverBar.location || 'main',
      product_id: selectedMaterial?.id || '',
      leftover_type: newLeftoverBar.leftover_type || 'blank',
      tip_diameter: newLeftoverBar.leftover_type === 'ground'
        ? (parseFloat(newLeftoverBar.tip_diameter) || 0)
        : undefined
    }]);

    setNewLeftoverBar({
      material_code: '',
      material_name: '',
      length: '',
      quantity: 1,
      location: 'main',
      product_id: '',
      leftover_type: 'blank',
      tip_diameter: ''
    });
    setShowAddLeftoverForm(false);
    setError(null);
  };

  const handleRemoveLeftoverBar = (barId) => {
    setRecordLeftoverBars(prev => prev.filter(bar => bar.id !== barId));
  };

  const handleUpdateLeftoverBar = (barId, field, value) => {
    setRecordLeftoverBars(prev => prev.map(bar => {
      if (bar.id !== barId) return bar;
      if (field === 'length') return { ...bar, length: parseFloat(value) || 0 };
      if (field === 'quantity') return { ...bar, quantity: parseInt(value) || 0 };
      if (field === 'location') return { ...bar, location: value };
      return bar;
    }));
  };

  const handleSubmitRecordLeftover = async () => {
    if (recordLeftoverBars.length === 0) {
      setError('请至少添加一条余料记录');
      setErrorType('destructive');
      return;
    }

    try {
      setRecordLeftoverLoading(true);
      setError(null);

      const payload = recordLeftoverBars.map(bar => ({
        material_code: bar.material_code,
        length: bar.length,
        quantity: bar.quantity,
        location: bar.location,
        product_id: bar.product_id,
        leftover_type: bar.leftover_type || 'blank',
        tip_diameter: bar.tip_diameter
      }));

      await productionService.recordAdditionalLeftover(
        id,
        payload,
        recordLeftoverJustification
      );

      await loadOrder();
      handleCloseRecordLeftoverDialog();
    } catch (err) {
      console.error('Failed to record additional leftover:', err);
      setError(err.response?.data?.error || err.message || '补录余料失败');
      setErrorType('destructive');
    } finally {
      setRecordLeftoverLoading(false);
    }
  };

  // BOM Assignment handlers
  const handleOpenAssignBomDialog = async (itemIndex) => {
    const item = order?.items?.[itemIndex];
    if (!item?.finished_product_id) {
      setError('无法获取产品信息');
      setErrorType('destructive');
      return;
    }

    setSelectedItemIndex(itemIndex);
    setAssignBomDialogOpen(true);
    setBomLoading(true);
    setSelectedBomId('');

    try {
      const boms = await bomService.getBomsByProduct(item.finished_product_id);
      // Only show active BOMs
      const activeBoms = (boms || []).filter(b => b.status === 'active');
      setAvailableBoms(activeBoms);
    } catch (err) {
      console.error('Failed to fetch BOMs:', err);
      setError('获取BOM列表失败');
      setErrorType('destructive');
    } finally {
      setBomLoading(false);
    }
  };

  const handleAssignBom = async () => {
    if (!selectedBomId) {
      setError('请选择一个BOM');
      setErrorType('destructive');
      return;
    }

    try {
      setActionLoading(true);
      await productionService.assignBomToItem(id, selectedItemIndex, selectedBomId);
      await loadOrder();
      setAssignBomDialogOpen(false);
      setSelectedBomId('');
      setSelectedItemIndex(null);
    } catch (err) {
      setError(`分配BOM失败: ${err.message}`);
      setErrorType('destructive');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveItem = async (itemIndex) => {
    if (!window.confirm('确定要删除此产品吗？')) {
      return;
    }

    try {
      setActionLoading(true);
      await productionService.removeItemFromOrder(id, itemIndex);
      await loadOrder();
    } catch (err) {
      setError(`删除产品失败: ${err.message}`);
      setErrorType('destructive');
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenBomSheet = (e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    const item = order?.items?.[selectedItemIndex];
    if (item) {
      setBomTargetProduct({
        product_id: item.finished_product_id,
        product_code: item.finished_product_code,
        product_name: item.finished_product_name,
      });
      // Close Dialog first, then open Sheet after a delay to avoid Radix focus trap conflicts
      setAssignBomDialogOpen(false);
      setTimeout(() => {
        setBomSheetOpen(true);
      }, 200);
    }
  };

  const handleOpenBomDetail = async (item) => {
    if (!item.bom_id) return;

    const bomId = item.bom_id?.$oid || item.bom_id?._id || item.bom_id;
    setBomDetailOpen(true);
    setBomDetailLoading(true);
    setBomDetailError(null);
    setBomDetailData(null);

    try {
      const bomData = await bomService.getBom(bomId);
      setBomDetailData(bomData);
    } catch (err) {
      console.error('Failed to load BOM detail:', err);
      setBomDetailError('加载BOM详情失败');
    } finally {
      setBomDetailLoading(false);
    }
  };

  const handleBomCreated = async (createdBom) => {
    setBomSheetOpen(false);
    // Auto-assign the newly created BOM to the current order item
    if (selectedItemIndex !== null && createdBom?._id) {
      try {
        setActionLoading(true);
        await productionService.assignBomToItem(id, selectedItemIndex, createdBom._id);
        await loadOrder();
        setSelectedBomId('');
        setSelectedItemIndex(null);
      } catch (err) {
        setError(`自动分配BOM失败: ${err.message}`);
        setErrorType('destructive');
      } finally {
        setActionLoading(false);
      }
    }
  };
  const handleOpenProductDrawer = async (productId) => {
    if (!productId) return;

    setProductDrawerOpen(true);
    setProductLoading(true);
    setProductError(null);
    setSelectedProduct(null);

    try {
      const productData = await productService.getProduct(productId);
      // Normalize MongoDB ObjectId format
      const normalizedData = {
        ...productData,
        _id: productData._id?.$oid || productData._id || null,
        created_at: productData.created_at?.$date || productData.created_at,
        updated_at: productData.updated_at?.$date || productData.updated_at,
      };
      setSelectedProduct(normalizedData);
    } catch (err) {
      console.error('Failed to load product:', err);
      setProductError('加载产品详情失败');
    } finally {
      setProductLoading(false);
    }
  };

  const handleCloseProductDrawer = () => {
    setProductDrawerOpen(false);
    setSelectedProduct(null);
    setProductError(null);
  };

  const getStatusBadge = (status) => {
    const config = {
      draft: { variant: 'secondary', label: '草稿' },
      released: { variant: 'default', label: '已发布' },
      in_progress: { variant: 'warning', label: '生产中' },
      completed: { variant: 'success', label: '已完成' },
      cancelled: { variant: 'destructive', label: '已取消' },
    };
    const { variant, label } = config[status] || { variant: 'secondary', label: status };
    return <Badge variant={variant}>{label}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const config = {
      low: { variant: 'outline', label: '低' },
      normal: { variant: 'secondary', label: '普通' },
      high: { variant: 'default', label: '高' },
      urgent: { variant: 'destructive', label: '紧急' },
    };
    const { variant, label } = config[priority] || { variant: 'secondary', label: priority };
    return <Badge variant={variant}>{label}</Badge>;
  };

  const statusSteps = [
    { key: 'draft', label: '草稿', icon: FileText },
    { key: 'released', label: '已发布', icon: Play },
    { key: 'in_progress', label: '生产中', icon: Factory },
    { key: 'completed', label: '已完成', icon: CheckCircle },
  ];

  const getCurrentStepIndex = () => {
    if (order?.status === 'cancelled') return -1;
    return statusSteps.findIndex((s) => s.key === order?.status);
  };

  const canManage = user && ['admin', 'warehouse_manager', 'sales_person', 'engineer'].includes(user.role);

  const hasPendingRequisition = materialRequisitions?.some(req => req.status === 'pending_approval');

  // Check for items without BOM
  const itemsWithoutBom = order?.items?.filter(item => !item.bom_id) || [];
  const hasItemsWithoutBom = itemsWithoutBom.length > 0 && order?.status === 'draft';

  if (loading && !order) {
    return (
      <Layout>
        <div className="container mx-auto py-6">
          <div className="flex justify-center items-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </div>
      </Layout>
    );
  }

  if (error && !order) {
    return (
      <Layout>
        <div className="container mx-auto py-6">
          <Alert variant={errorType || 'destructive'}>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <Button onClick={handleBack} className="mt-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            返回生产工单
          </Button>
        </div>
      </Layout>
    );
  }

  const currentStepIndex = getCurrentStepIndex();

  const formatDate = (dateValue) => {
    if (!dateValue) return '-';
    if (typeof dateValue === 'string') return new Date(dateValue).toLocaleString();
    if (dateValue.$date) return new Date(dateValue.$date).toLocaleString();
    return new Date(dateValue).toLocaleString();
  };

  return (
    <Layout>
      <div className="container mx-auto py-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={handleBack}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <div className="flex items-center gap-3">
                {isEditing ? (
                  <Input
                    value={editProductionOrderId}
                    onChange={(e) => setEditProductionOrderId(e.target.value)}
                    className="text-2xl font-bold h-auto py-1 w-64"
                    placeholder="工单号"
                  />
                ) : (
                  <h1 className="text-2xl font-bold">{order?.production_order_id}</h1>
                )}
                {order && getStatusBadge(order.status)}
                {isEditing ? (
                  <Select value={editPriority} onValueChange={setEditPriority}>
                    <SelectTrigger className="w-28 h-8">
                      <SelectValue placeholder="优先级" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">低</SelectItem>
                      <SelectItem value="normal">普通</SelectItem>
                      <SelectItem value="high">高</SelectItem>
                      <SelectItem value="urgent">紧急</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  order && getPriorityBadge(order.priority)
                )}
                {order?.status === 'completed' && order?.leftover_override && (
                  <Badge variant="warning" className="flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    余料已修改
                  </Badge>
                )}
              </div>
              <p className="text-muted-foreground">
                {order?.finished_product_name} ({order?.finished_product_code})
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setPrintDialogOpen(true)}>
              <Printer className="h-4 w-4 mr-2" />
              打印
            </Button>
            <Button variant="outline" onClick={handleExportToExcel}>
              <Download className="h-4 w-4 mr-2" />
              导出Excel
            </Button>
            {canManage && ['draft', 'released'].includes(order?.status) && !isEditing && (
              <Button variant="outline" onClick={handleStartEdit}>
                <Edit className="h-4 w-4 mr-2" />
                编辑
              </Button>
            )}
            {isEditing && (
              <>
                <Button variant="outline" onClick={handleCancelEdit} disabled={editSaving}>
                  取消
                </Button>
                <Button onClick={handleSaveEdit} disabled={editSaving}>
                  {editSaving ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  保存
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant={errorType || 'destructive'}>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="whitespace-pre-wrap">{error}</AlertDescription>
          </Alert>
        )}

        {/* Warning: Items without BOM */}
        {hasItemsWithoutBom && (
          <Alert variant="warning">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              有 {itemsWithoutBom.length} 个产品尚未分配BOM。请在发布生产前为所有产品分配BOM。
            </AlertDescription>
          </Alert>
        )}

        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">实际开始时间</p>
                {isEditing ? (
                  <Input
                    type="date"
                    value={editActualStartDate}
                    onChange={(e) => setEditActualStartDate(e.target.value)}
                    className="mt-1"
                  />
                ) : (
                  <p className="font-medium">
                    {order?.actual_start_date
                      ? new Date(order.actual_start_date).toLocaleDateString('zh-CN')
                      : '-'}
                  </p>
                )}
              </div>
              <div>
                <p className="text-sm text-muted-foreground">实际结束时间</p>
                {isEditing ? (
                  <Input
                    type="date"
                    value={editActualEndDate}
                    onChange={(e) => setEditActualEndDate(e.target.value)}
                    className="mt-1"
                  />
                ) : (
                  <p className="font-medium">
                    {order?.actual_end_date
                      ? new Date(order.actual_end_date).toLocaleDateString('zh-CN')
                      : '-'}
                  </p>
                )}
              </div>
              <div>
                <p className="text-sm text-muted-foreground">计划开始时间</p>
                {isEditing ? (
                  <Input
                    type="date"
                    value={editPlannedStartDate}
                    onChange={(e) => setEditPlannedStartDate(e.target.value)}
                    className="mt-1"
                  />
                ) : (
                  <p className="font-medium">
                    {order?.planned_start_date
                      ? new Date(order.planned_start_date).toLocaleDateString('zh-CN')
                      : '-'}
                  </p>
                )}
              </div>
              <div>
                <p className="text-sm text-muted-foreground">计划结束时间</p>
                {isEditing ? (
                  <Input
                    type="date"
                    value={editPlannedEndDate}
                    onChange={(e) => setEditPlannedEndDate(e.target.value)}
                    className="mt-1"
                  />
                ) : (
                  <p className="font-medium">
                    {order?.planned_end_date
                      ? new Date(order.planned_end_date).toLocaleDateString('zh-CN')
                      : '-'}
                  </p>
                )}
              </div>
              <div>
                <p className="text-sm text-muted-foreground">关联销售订单</p>
                {order?.source_type === 'sales_order' && order?.source_id ? (
                  <button
                    onClick={() => navigate(`/sales/${order.source_id}/view`)}
                    className="font-medium text-primary hover:underline flex items-center gap-1 mt-1"
                  >
                    {order.source_reference || '-'}
                    <ExternalLink className="h-3 w-3" />
                  </button>
                ) : (
                  <p className="font-medium text-muted-foreground">-</p>
                )}
              </div>
              <div>
                <p className="text-sm text-muted-foreground">客户</p>
                <p className="font-medium">{order?.client_name || '-'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Status Timeline */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-between items-center">
              {statusSteps.map((step, index) => {
                const Icon = step.icon;
                const isActive = currentStepIndex >= index;
                const isCurrent = currentStepIndex === index;

                return (
                  <React.Fragment key={step.key}>
                    <div className="flex flex-col items-center">
                      <div
                        className={`
                          w-12 h-12 rounded-full flex items-center justify-center
                          ${
                            isActive
                              ? isCurrent
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-primary/20 text-primary'
                              : 'bg-muted text-muted-foreground'
                          }
                        `}
                      >
                        <Icon className="h-6 w-6" />
                      </div>
                      <span
                        className={`mt-2 text-sm ${isCurrent ? 'font-medium' : 'text-muted-foreground'}`}
                      >
                        {step.label}
                      </span>
                    </div>
                    {index < statusSteps.length - 1 && (
                      <div
                        className={`flex-1 h-1 mx-4 ${
                          currentStepIndex > index ? 'bg-primary' : 'bg-muted'
                        }`}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            {order?.status === 'cancelled' && (
              <div className="mt-4 p-4 bg-destructive/10 rounded-lg text-center">
                <XCircle className="h-6 w-6 text-destructive mx-auto mb-2" />
                <span className="text-destructive font-medium">工单已取消</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Leftover Override Warning */}
        {order?.status === 'completed' && order?.leftover_override && (
          <Card className="border-amber-200 bg-amber-50/50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-amber-800">余料数据已手动修改</span>
                    <Badge variant="warning">Override</Badge>
                  </div>
                  <p className="text-sm text-amber-700">
                    <span className="font-medium">修改理由：</span>
                    {order.leftover_override_justification || '未提供理由'}
                  </p>
                  <p className="text-xs text-amber-600">
                    系统根据BOM和领料记录自动计算的理论余料与实际录入不一致。请核查成本影响。
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Finished Goods Receipt Record for Completed Orders */}
        {order?.status === 'completed' && order?.finished_receipt_movements && order.finished_receipt_movements.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Package className="h-5 w-5" />
                成品入库记录
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>时间</TableHead>
                    <TableHead>产品</TableHead>
                    <TableHead className="text-right">数量</TableHead>
                    <TableHead>库位</TableHead>
                    <TableHead>备注</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {order.finished_receipt_movements.map((mov, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="text-sm whitespace-nowrap">
                        {formatDate(mov.created_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          {mov.products?.map((prod, pIdx) => (
                            <div key={pIdx} className="flex flex-col">
                              <span className="font-medium">{prod.product_id || '-'}</span>
                              <span className="text-xs text-muted-foreground">{prod.name || '-'}</span>
                            </div>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {mov.products?.map((prod, pIdx) => (
                          <div key={pIdx}>{prod.quantity}</div>
                        ))}
                      </TableCell>
                      <TableCell>{mov.warehouse_location || '-'}</TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">{mov.notes || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Remaining Bar Info for Completed Orders */}
        {order?.status === 'completed' && order?.remaining_bar_data && order.remaining_bar_data.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Package className="h-5 w-5" />
                余料入库记录
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>物料</TableHead>
                    <TableHead className="text-right">长度(mm)</TableHead>
                    <TableHead className="text-right">数量</TableHead>
                    <TableHead>库位</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {order.remaining_bar_data.map((bar, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-mono text-sm">{bar.material_code || bar.product_id}</TableCell>
                      <TableCell className="text-right">{bar.length}</TableCell>
                      <TableCell className="text-right">{bar.quantity}</TableCell>
                      <TableCell>{bar.location || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {order.calculated_remaining_length > 0 && (
                <div className="mt-3 text-xs text-muted-foreground">
                  系统理论计算余料: {Math.round(order.calculated_remaining_length * 100) / 100}mm
                  {order.remaining_bar_length > 0 && (
                    <span> | 实际录入余料: {Math.round(order.remaining_bar_length * 100) / 100}mm</span>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Action Buttons */}
        {canManage && !['completed', 'cancelled'].includes(order?.status) && (
          <Card>
            <CardContent className="pt-6">
              <div className="flex gap-3">
                {order?.status === 'draft' && (
                  <Button 
                    onClick={handleRelease} 
                    disabled={actionLoading || hasItemsWithoutBom}
                    title={hasItemsWithoutBom ? '请先为所有产品分配BOM' : ''}
                  >
                    <Play className="h-4 w-4 mr-2" />
                    发布生产
                  </Button>
                )}
                {order?.status === 'released' && (
                  <>
                    <Button
                      onClick={() => setIssueDialogOpen(true)}
                      disabled={actionLoading || hasPendingRequisition}
                      title={hasPendingRequisition ? '存在待审批的领料申请，请等待审批完成后再提交新的领料申请' : ''}
                    >
                      <Truck className="h-4 w-4 mr-2" />
                      领料
                    </Button>
                    <Button
                      onClick={handleStartProduction}
                      disabled={actionLoading}
                    >
                      <Factory className="h-4 w-4 mr-2" />
                      开始生产
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleOpenAdditionalReceiptDialog}
                      disabled={actionLoading}
                    >
                      <PackagePlus className="h-4 w-4 mr-2" />
                      部分成品入库
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleOpenPartialLeftoverDialog}
                      disabled={actionLoading}
                    >
                      <PackagePlus className="h-4 w-4 mr-2" />
                      部分余料入库
                    </Button>
                    <Button onClick={() => setCompleteDialogOpen(true)} disabled={actionLoading}>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      完成生产
                    </Button>
                  </>
                )}
                {order?.status === 'in_progress' && (
                  <>
                    <Button
                      onClick={() => setIssueDialogOpen(true)}
                      variant="outline"
                      disabled={actionLoading || hasPendingRequisition}
                      title={hasPendingRequisition ? '存在待审批的领料申请，请等待审批完成后再提交新的领料申请' : ''}
                    >
                      <Truck className="h-4 w-4 mr-2" />
                      追加领料
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleOpenAdditionalReceiptDialog}
                      disabled={actionLoading}
                    >
                      <PackagePlus className="h-4 w-4 mr-2" />
                      部分成品入库
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleOpenPartialLeftoverDialog}
                      disabled={actionLoading}
                    >
                      <PackagePlus className="h-4 w-4 mr-2" />
                      部分余料入库
                    </Button>
                    <Button onClick={() => setCompleteDialogOpen(true)} disabled={actionLoading}>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      完成生产
                    </Button>
                  </>
                )}
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  disabled={actionLoading}
                  className="text-destructive hover:text-destructive"
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  取消工单
                </Button>
                {user?.role === 'admin' && (
                  <Button
                    variant="ghost"
                    onClick={handleRefreshProcessSteps}
                    disabled={actionLoading}
                    className="ml-auto"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    刷新工序步骤
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Record Additional Leftover Button for Completed Orders */}
        {canManage && order?.status === 'completed' && (
          <Card>
            <CardContent className="pt-6">
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={handleOpenAdditionalReceiptDialog}
                  disabled={actionLoading}
                >
                  <PackagePlus className="h-4 w-4 mr-2" />
                  部分成品入库
                </Button>
                <Button
                  variant="outline"
                  onClick={handleOpenRecordLeftoverDialog}
                  disabled={actionLoading}
                >
                  <PackagePlus className="h-4 w-4 mr-2" />
                  补录余料
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Order Items with BOM Assignment */}
        {order?.items?.length > 0 && !isEditing && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                产品清单
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12 text-center">#</TableHead>
                    <TableHead>产品代码</TableHead>
                    <TableHead>产品名称</TableHead>
                    <TableHead>规格</TableHead>
                    <TableHead>计划数量</TableHead>
                    <TableHead>BOM状态</TableHead>
                    {order?.status === 'draft' && canManage && <TableHead>操作</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {order.items.map((item, itemIndex) => (
                    <TableRow key={itemIndex}>
                      <TableCell className="text-center font-medium text-muted-foreground">{itemIndex + 1}</TableCell>
                      <TableCell>
                        <button
                          onClick={() => handleOpenProductDrawer(item.finished_product_id)}
                          className="font-mono text-primary hover:underline cursor-pointer"
                        >
                          {item.finished_product_code}
                        </button>
                      </TableCell>
                      <TableCell>{item.finished_product_name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {item.product_details?.specification || '-'}
                      </TableCell>
                      <TableCell>{item.planned_quantity}</TableCell>
                      <TableCell>
                        {item.bom_id ? (
                          <Badge
                            variant="success"
                            className="cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => handleOpenBomDetail(item)}
                          >
                            {item.bom_code || '已分配'}
                          </Badge>
                        ) : (
                          <Badge variant="destructive">未分配BOM</Badge>
                        )}
                      </TableCell>
                      {order?.status === 'draft' && canManage && (
                        <TableCell>
                          <div className="flex gap-2">
                            {!item.bom_id && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleOpenAssignBomDialog(itemIndex)}
                                disabled={actionLoading}
                              >
                                <Link className="h-4 w-4 mr-1" />
                                分配BOM
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveItem(itemIndex)}
                              disabled={actionLoading}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {isEditing && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                编辑产品清单
              </CardTitle>
            </CardHeader>
            <CardContent>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={editItems.map((item) => item._editId)}
                  strategy={verticalListSortingStrategy}
                >
                  <Table>
                    <TableHeader>
                      <TableRow>
                      <TableHead className="w-12 text-center">#</TableHead>
                      <TableHead className="w-[50px]">排序</TableHead>
                      <TableHead>产品代码</TableHead>
                      <TableHead>产品名称</TableHead>
                      <TableHead>规格</TableHead>
                      <TableHead className="w-32">计划数量</TableHead>
                      <TableHead>BOM状态</TableHead>
                      <TableHead className="w-20">操作</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {editItems.map((item, index) => (
                        <SortableTableRow
                          key={item._editId}
                          id={item._editId}
                          showDragHandle={true}
                        >
                          <TableCell className="text-center font-medium text-muted-foreground">{index + 1}</TableCell>
                          <TableCell>
                            <button
                              onClick={() => handleOpenProductDrawer(item.finished_product_id)}
                              className="font-mono text-primary hover:underline cursor-pointer"
                            >
                              {item.finished_product_code}
                            </button>
                          </TableCell>
                          <TableCell>{item.finished_product_name}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {item.product_details?.specification || item.specification || '-'}
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              value={item.planned_quantity}
                              onChange={(e) => handleEditQuantityChange(item._editId, e.target.value)}
                              min={1}
                              className="w-24"
                            />
                          </TableCell>
                          <TableCell>
                            {item.bom_id ? (
                              <Badge
                                variant="success"
                                className="cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={() => handleOpenBomDetail(item)}
                              >
                                {item.bom_code || '已分配'}
                              </Badge>
                            ) : (
                              <Badge variant="destructive">未分配BOM</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveEditItem(item._editId)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </SortableTableRow>
                      ))}
                      {editItems.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                            暂无产品项目，请点击下方按钮添加
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </SortableContext>
              </DndContext>

              <div className="mt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setProductSearchTerm('');
                    setProductSearchOpen(true);
                  }}
                  className="w-full"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  添加产品
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Material Issue History */}
        {linkedMovements.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5" />
                领料历史详情
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>时间</TableHead>
                    <TableHead>物料</TableHead>
                    <TableHead className="text-right">数量</TableHead>
                    <TableHead>仓库/位置</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {linkedMovements.map((move, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="text-sm whitespace-nowrap">
                        {formatDate(move.created_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          {move.products?.map((prod, pIdx) => (
                            <div key={pIdx} className="flex flex-col">
                              <span className="font-medium">{prod.product_id || prod.material_product_code || '-'}</span>
                              <span className="text-xs text-muted-foreground">{prod.name || prod.product_name || '-'}</span>
                            </div>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {move.products?.reduce((sum, p) => sum + (p.quantity || 0), 0) || 0}
                      </TableCell>
                      <TableCell>{move.warehouse_location || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {materialRequisitions.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                领料申请记录
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>申请号</TableHead>
                    <TableHead>状态</TableHead>
                    <TableHead>物料项数</TableHead>
                    <TableHead>申请时间</TableHead>
                    <TableHead>操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {materialRequisitions.map((req, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-mono">{req.requisition_number}</TableCell>
                      <TableCell>
                        <Badge 
                          variant={
                            req.status === 'approved' ? 'default' :
                            req.status === 'pending_approval' ? 'secondary' :
                            req.status === 'rejected' ? 'destructive' : 'outline'
                          }
                        >
                          {req.status === 'approved' ? '已通过' :
                           req.status === 'pending_approval' ? '待审批' :
                           req.status === 'rejected' ? '已拒绝' :
                           req.status === 'cancelled' ? '已取消' : req.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{req.products?.length || 0} 项</TableCell>
                      <TableCell>{formatDate(req.created_at)}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/material-requisitions/${req._id}/view`)}
                        >
                          查看详情
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {order?.status_history?.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5" />
                操作历史
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>时间</TableHead>
                    <TableHead>原状态</TableHead>
                    <TableHead>新状态</TableHead>
                    <TableHead>操作员</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {order.status_history.map((entry, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="text-sm whitespace-nowrap">
                        {formatDate(entry.changed_at)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{getProductionStatusLabel(entry.from_status)}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="default">{getProductionStatusLabel(entry.to_status)}</Badge>
                      </TableCell>
                      <TableCell>{entry.changed_by_name || entry.changed_by || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Process Steps */}
        {order?.items?.length > 0 && (
          <>
            {['released', 'in_progress'].includes(order?.status) && (
              <div className="flex justify-end mb-4">
                <Button
                  variant="outline"
                  onClick={() => setSubcontractDialogOpen(true)}
                  className="border-amber-500 text-amber-700 hover:bg-amber-50"
                >
                  <Truck className="h-4 w-4 mr-2" />
                  申请外协
                </Button>
              </div>
            )}

            {/* Linked Subcontract Orders */}
            {(() => {
              const allSubcontracts = [];
              order.items.forEach((item, itemIdx) => {
                (item.process_steps || []).forEach((step) => {
                  (step.subcontract_orders || []).forEach((sc) => {
                    allSubcontracts.push({
                      ...sc,
                      _stepName: step.name,
                      _stepSequence: step.sequence,
                      _itemIndex: itemIdx,
                      _productCode: item.finished_product_code,
                    });
                  });
                });
              });
              if (allSubcontracts.length === 0) return null;
              return (
                <Card className="mb-6">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Truck className="h-5 w-5 text-amber-600" />
                      关联外协单
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>外协单号</TableHead>
                          <TableHead>工序</TableHead>
                          <TableHead>供应商</TableHead>
                          <TableHead className="text-center">外协数量</TableHead>
                          <TableHead className="text-center">状态</TableHead>
                          <TableHead className="text-center">操作</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {allSubcontracts.map((sc, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">
                              <Button
                                variant="link"
                                className="p-0 h-auto"
                                onClick={() => navigate(`/subcontract/${sc.subcontract_order_id}`)}
                              >
                                {sc.subcontract_order_id}
                              </Button>
                            </TableCell>
                            <TableCell>
                              {sc._stepName}
                              {sc._productCode && (
                                <span className="text-xs text-muted-foreground block">
                                  {sc._productCode}
                                </span>
                              )}
                            </TableCell>
                            <TableCell>{sc.supplier_name}</TableCell>
                            <TableCell className="text-center">{sc.quantity}</TableCell>
                            <TableCell className="text-center">
                              <Badge
                                variant={
                                  sc.status === 'completed'
                                    ? 'success'
                                    : sc.status === 'in_progress'
                                    ? 'default'
                                    : sc.status === 'issued'
                                    ? 'warning'
                                    : sc.status === 'cancelled'
                                    ? 'destructive'
                                    : 'secondary'
                                }
                              >
                                {sc.status === 'pending' ? '待处理'
                                  : sc.status === 'issued' ? '已发料'
                                  : sc.status === 'in_progress' ? '加工中'
                                  : sc.status === 'completed' ? '已完成'
                                  : sc.status === 'cancelled' ? '已取消'
                                  : sc.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate(`/subcontract/${sc.subcontract_order_id}`)}
                              >
                                <ExternalLink className="h-4 w-4 mr-1" />
                                查看
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              );
            })()}

            {order.items.map((item, itemIndex) => (
              <Card key={itemIndex}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="h-5 w-5" />
                    工序进度跟踪 - {item.finished_product_name} ({item.finished_product_code})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {item.process_steps && item.process_steps.length > 0 ? (
                    <ProductionScheduleStepTracker
                      progress={scheduleProgress?.items?.[itemIndex]}
                    />
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <p className="mb-2">暂无工序步骤</p>
                      {user?.role === 'admin' && item.bom_id && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleRefreshProcessSteps}
                          disabled={actionLoading}
                        >
                          <RefreshCw className="h-4 w-4 mr-2" />
                          刷新工序步骤
                        </Button>
                      )}
                      {!item.bom_id && (
                        <p className="text-sm text-muted-foreground">
                          请先分配BOM以查看工序步骤
                        </p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </>
        )}
      </div>

      {/* BOM Assignment Dialog */}
      <Dialog key="assign-bom-dialog" open={assignBomDialogOpen} onOpenChange={setAssignBomDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>分配BOM</DialogTitle>
            <DialogDescription>
              为产品 {order?.items?.[selectedItemIndex]?.finished_product_code} 选择BOM
            </DialogDescription>
          </DialogHeader>
          
          {bomLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : availableBoms.length === 0 ? (
            <div className="py-4 space-y-4">
              <Alert variant="destructive">
                <AlertDescription>
                  该产品没有可用的BOM。请先创建BOM。
                </AlertDescription>
              </Alert>
              <Button 
                onClick={(e) => handleOpenBomSheet(e)}
                className="w-full"
              >
                <Plus className="h-4 w-4 mr-2" />
                创建BOM
              </Button>
            </div>
          ) : (
            <div className="py-4">
              <Select value={selectedBomId} onValueChange={setSelectedBomId}>
                <SelectTrigger>
                  <SelectValue placeholder="选择BOM" />
                </SelectTrigger>
                <SelectContent>
                  {availableBoms.map((bom) => (
                    <SelectItem key={bom._id} value={bom._id}>
                      {bom.bom_id} {bom.is_default && '(默认)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAssignBomDialogOpen(false)}
              disabled={actionLoading}
            >
              取消
            </Button>
            <Button
              onClick={handleAssignBom}
              disabled={actionLoading || !selectedBomId || availableBoms.length === 0}
            >
              {actionLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              确认分配
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Issue Materials Dialog */}
      <MaterialIssueDialog
        open={issueDialogOpen}
        onClose={() => setIssueDialogOpen(false)}
        workOrder={order}
        onSuccess={loadOrder}
      />

      {/* Complete Work Order Dialog */}
      <CompleteWorkOrderDialog
        open={completeDialogOpen}
        onClose={() => setCompleteDialogOpen(false)}
        workOrder={order}
        onSuccess={handleCompleteSuccess}
      />

      {/* Record Additional Leftover Dialog */}
      <Dialog open={recordLeftoverDialogOpen} onOpenChange={handleCloseRecordLeftoverDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PackagePlus className="h-5 w-5" />
              补录余料
            </DialogTitle>
            <DialogDescription>
              为已完成的工单 {order?.production_order_id} 补录余料入库
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Add New Bar Form */}
            <div className="flex items-center justify-between">
              <span className="text-base font-medium">余料录入</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAddLeftoverForm(true)}
                disabled={showAddLeftoverForm || recordLeftoverLoading}
              >
                <Plus className="h-4 w-4 mr-1" />
                添加余料
              </Button>
            </div>

            {showAddLeftoverForm && (
              <Card className="p-3 border-dashed">
                <div className="space-y-3">
                  <div className="space-y-1">
                    <span className="text-xs text-muted-foreground">选择物料</span>
                    <Select
                      value={newLeftoverBar.material_code}
                      onValueChange={(value) => {
                        const availableMaterials = getAvailableMaterialsForLeftover();
                        const material = availableMaterials.find(m => m.code === value);
                        setNewLeftoverBar({
                          ...newLeftoverBar,
                          material_code: value,
                          material_name: material?.name || '',
                          product_id: material?.id || ''
                        });
                      }}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue placeholder="选择物料类型" />
                      </SelectTrigger>
                      <SelectContent>
                        {getAvailableMaterialsForLeftover().map((material) => (
                          <SelectItem key={material.code} value={material.code}>
                            {material.code} {material.name ? `- ${material.name}` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1">
                    <span className="text-xs text-muted-foreground">余料类型</span>
                    <Select
                      value={newLeftoverBar.leftover_type}
                      onValueChange={(value) => {
                        setNewLeftoverBar({
                          ...newLeftoverBar,
                          leftover_type: value,
                          tip_diameter: value === 'blank' ? '' : newLeftoverBar.tip_diameter
                        });
                      }}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue placeholder="选择余料类型" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="blank">毛坯余料</SelectItem>
                        <SelectItem value="ground">精磨余料</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <span className="text-xs text-muted-foreground">长度(mm)</span>
                      <Input
                        type="number"
                        value={newLeftoverBar.length}
                        onChange={(e) => setNewLeftoverBar({...newLeftoverBar, length: e.target.value})}
                        placeholder="长度"
                        className="h-8"
                      />
                    </div>
                    <div className="space-y-1">
                      <span className="text-xs text-muted-foreground">数量(根)</span>
                      <Input
                        type="number"
                        value={newLeftoverBar.quantity}
                        onChange={(e) => setNewLeftoverBar({...newLeftoverBar, quantity: e.target.value})}
                        placeholder="数量"
                        className="h-8"
                      />
                    </div>
                  </div>

                  {newLeftoverBar.leftover_type === 'ground' && (
                    <div className="space-y-1">
                      <span className="text-xs text-muted-foreground">刃径(mm)</span>
                      <Input
                        type="number"
                        step="0.01"
                        value={newLeftoverBar.tip_diameter}
                        onChange={(e) => setNewLeftoverBar({...newLeftoverBar, tip_diameter: e.target.value})}
                        placeholder="精磨后刃径"
                        className="h-8"
                      />
                      <p className="text-[10px] text-muted-foreground">精磨余料需手动输入实际刃径</p>
                    </div>
                  )}

                  <div className="space-y-1">
                    <span className="text-xs text-muted-foreground">位置</span>
                    <Input
                      value={newLeftoverBar.location}
                      onChange={(e) => setNewLeftoverBar({...newLeftoverBar, location: e.target.value})}
                      placeholder="位置"
                      className="h-8"
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setShowAddLeftoverForm(false)}>取消</Button>
                    <Button size="sm" onClick={handleAddLeftoverBar}>确认添加</Button>
                  </div>
                </div>
              </Card>
            )}

            {/* Bars Table */}
            <div className="border rounded-md overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>物料</TableHead>
                    <TableHead>类型</TableHead>
                    <TableHead>长度</TableHead>
                    <TableHead>数量</TableHead>
                    <TableHead>库位</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recordLeftoverBars.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground text-sm py-4">
                        暂无余料记录，请点击上方按钮添加
                      </TableCell>
                    </TableRow>
                  ) : (
                    recordLeftoverBars.map((bar) => (
                      <TableRow key={bar.id}>
                        <TableCell className="font-medium">
                          <div className="flex flex-col">
                            <span>{bar.material_code}</span>
                            {bar.leftover_type === 'ground' && bar.tip_diameter > 0 && (
                              <span className="text-xs text-blue-600">刃径 {bar.tip_diameter}mm</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={bar.leftover_type === 'ground' ? 'default' : 'secondary'} className="text-xs">
                            {bar.leftover_type === 'ground' ? '精磨' : '毛坯'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            value={bar.length}
                            onChange={(e) => handleUpdateLeftoverBar(bar.id, 'length', e.target.value)}
                            className="w-20 h-8"
                            disabled={recordLeftoverLoading}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            value={bar.quantity}
                            onChange={(e) => handleUpdateLeftoverBar(bar.id, 'quantity', e.target.value)}
                            className="w-16 h-8"
                            disabled={recordLeftoverLoading}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={bar.location}
                            onChange={(e) => handleUpdateLeftoverBar(bar.id, 'location', e.target.value)}
                            className="w-28 h-8"
                            placeholder="库位"
                            disabled={recordLeftoverLoading}
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive"
                            onClick={() => handleRemoveLeftoverBar(bar.id)}
                            disabled={recordLeftoverLoading}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Justification */}
            <div className="space-y-2">
              <span className="text-sm font-medium text-amber-600">
                补录理由 <span className="text-destructive">*</span>
              </span>
              <Input
                value={recordLeftoverJustification}
                onChange={(e) => setRecordLeftoverJustification(e.target.value)}
                placeholder="请说明补录余料的原因（必填）"
                disabled={recordLeftoverLoading}
              />
              <p className="text-xs text-muted-foreground">
                工单已完成，补录余料将标记为手动修改记录，请说明原因以便成本分析。
              </p>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={handleCloseRecordLeftoverDialog} disabled={recordLeftoverLoading}>
                取消
              </Button>
              <Button
                onClick={handleSubmitRecordLeftover}
                disabled={recordLeftoverLoading || recordLeftoverBars.length === 0 || !recordLeftoverJustification.trim()}
              >
                {recordLeftoverLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                确认补录
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Partial Leftover Receipt Dialog (余料部分入库) */}
      <Dialog open={partialLeftoverDialogOpen} onOpenChange={handleClosePartialLeftoverDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PackagePlus className="h-5 w-5" />
              部分余料入库
            </DialogTitle>
            <DialogDescription>
              为生产中的工单 {order?.production_order_id} 入库余料
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Add New Bar Form */}
            <div className="flex items-center justify-between">
              <span className="text-base font-medium">余料录入</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAddLeftoverForm(true)}
                disabled={showAddLeftoverForm || partialLeftoverLoading}
              >
                <Plus className="h-4 w-4 mr-1" />
                添加余料
              </Button>
            </div>

            {showAddLeftoverForm && (
              <Card className="p-3 border-dashed">
                <div className="space-y-3">
                  <div className="space-y-1">
                    <span className="text-xs text-muted-foreground">选择物料</span>
                    <Select
                      value={newLeftoverBar.material_code}
                      onValueChange={(value) => {
                        const availableMaterials = getAvailableMaterialsForLeftover();
                        const material = availableMaterials.find(m => m.code === value);
                        setNewLeftoverBar({
                          ...newLeftoverBar,
                          material_code: value,
                          material_name: material?.name || '',
                          product_id: material?.id || ''
                        });
                      }}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue placeholder="选择物料类型" />
                      </SelectTrigger>
                      <SelectContent>
                        {getAvailableMaterialsForLeftover().map((material) => (
                          <SelectItem key={material.code} value={material.code}>
                            {material.code} {material.name ? `- ${material.name}` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1">
                    <span className="text-xs text-muted-foreground">余料类型</span>
                    <Select
                      value={newLeftoverBar.leftover_type}
                      onValueChange={(value) => {
                        setNewLeftoverBar({
                          ...newLeftoverBar,
                          leftover_type: value,
                          tip_diameter: value === 'blank' ? '' : newLeftoverBar.tip_diameter
                        });
                      }}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue placeholder="选择余料类型" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="blank">毛坯余料</SelectItem>
                        <SelectItem value="ground">精磨余料</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <span className="text-xs text-muted-foreground">长度(mm)</span>
                      <Input
                        type="number"
                        value={newLeftoverBar.length}
                        onChange={(e) => setNewLeftoverBar({...newLeftoverBar, length: e.target.value})}
                        placeholder="长度"
                        className="h-8"
                      />
                    </div>
                    <div className="space-y-1">
                      <span className="text-xs text-muted-foreground">数量(根)</span>
                      <Input
                        type="number"
                        value={newLeftoverBar.quantity}
                        onChange={(e) => setNewLeftoverBar({...newLeftoverBar, quantity: e.target.value})}
                        placeholder="数量"
                        className="h-8"
                      />
                    </div>
                  </div>

                  {newLeftoverBar.leftover_type === 'ground' && (
                    <div className="space-y-1">
                      <span className="text-xs text-muted-foreground">刃径(mm)</span>
                      <Input
                        type="number"
                        step="0.01"
                        value={newLeftoverBar.tip_diameter}
                        onChange={(e) => setNewLeftoverBar({...newLeftoverBar, tip_diameter: e.target.value})}
                        placeholder="精磨后刃径"
                        className="h-8"
                      />
                      <p className="text-[10px] text-muted-foreground">精磨余料需手动输入实际刃径</p>
                    </div>
                  )}

                  <div className="space-y-1">
                    <span className="text-xs text-muted-foreground">位置</span>
                    <Input
                      value={newLeftoverBar.location}
                      onChange={(e) => setNewLeftoverBar({...newLeftoverBar, location: e.target.value})}
                      placeholder="位置"
                      className="h-8"
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setShowAddLeftoverForm(false)}>取消</Button>
                    <Button size="sm" onClick={handleAddPartialLeftoverBar}>确认添加</Button>
                  </div>
                </div>
              </Card>
            )}

            {/* Bars Table */}
            <div className="border rounded-md overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>物料</TableHead>
                    <TableHead>类型</TableHead>
                    <TableHead>长度</TableHead>
                    <TableHead>数量</TableHead>
                    <TableHead>库位</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {partialLeftoverBars.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground text-sm py-4">
                        暂无余料记录，请点击上方按钮添加
                      </TableCell>
                    </TableRow>
                  ) : (
                    partialLeftoverBars.map((bar) => (
                      <TableRow key={bar.id}>
                        <TableCell className="font-medium">
                          <div className="flex flex-col">
                            <span>{bar.material_code}</span>
                            {bar.leftover_type === 'ground' && bar.tip_diameter > 0 && (
                              <span className="text-xs text-blue-600">刃径 {bar.tip_diameter}mm</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={bar.leftover_type === 'ground' ? 'default' : 'secondary'} className="text-xs">
                            {bar.leftover_type === 'ground' ? '精磨' : '毛坯'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            value={bar.length}
                            onChange={(e) => handleUpdatePartialLeftoverBar(bar.id, 'length', e.target.value)}
                            className="w-20 h-8"
                            disabled={partialLeftoverLoading}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            value={bar.quantity}
                            onChange={(e) => handleUpdatePartialLeftoverBar(bar.id, 'quantity', e.target.value)}
                            className="w-16 h-8"
                            disabled={partialLeftoverLoading}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={bar.location}
                            onChange={(e) => handleUpdatePartialLeftoverBar(bar.id, 'location', e.target.value)}
                            className="w-28 h-8"
                            placeholder="库位"
                            disabled={partialLeftoverLoading}
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive"
                            onClick={() => handleRemovePartialLeftoverBar(bar.id)}
                            disabled={partialLeftoverLoading}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <span className="text-sm font-medium">备注</span>
              <Input
                value={partialLeftoverNotes}
                onChange={(e) => setPartialLeftoverNotes(e.target.value)}
                placeholder="可选：输入余料入库原因或备注"
                disabled={partialLeftoverLoading}
              />
              <p className="text-xs text-muted-foreground">
                余料入库将更新余料库存，工单状态保持不变。
              </p>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={handleClosePartialLeftoverDialog} disabled={partialLeftoverLoading}>
                取消
              </Button>
              <Button
                onClick={handleSubmitPartialLeftover}
                disabled={partialLeftoverLoading || partialLeftoverBars.length === 0}
              >
                {partialLeftoverLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                确认入库
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* BOM Creation Drawer */}
      <BomCreationDrawer
        open={bomSheetOpen}
        onOpenChange={setBomSheetOpen}
        finishedProduct={bomTargetProduct}
        onSuccess={handleBomCreated}
        onCancel={() => setBomSheetOpen(false)}
      />

      {/* BOM Detail Drawer */}
      <Sheet open={bomDetailOpen} onOpenChange={setBomDetailOpen}>
        <SheetContent side="right" className="w-[90vw] max-w-5xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Layers className="h-5 w-5" />
              {bomDetailData ? bomDetailData.bom_id : 'BOM详情'}
            </SheetTitle>
            <SheetDescription>
              {bomDetailData
                ? `${bomDetailData.finished_product_name} (${bomDetailData.finished_product_code})`
                : ''}
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-6">
            {bomDetailLoading ? (
              <div className="flex justify-center items-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : bomDetailError ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{bomDetailError}</AlertDescription>
              </Alert>
            ) : bomDetailData ? (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="pt-6">
                      <p className="text-sm text-muted-foreground">版本</p>
                      <p className="text-2xl font-bold">V{bomDetailData.version || 1}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <p className="text-sm text-muted-foreground">产出数量</p>
                      <p className="text-2xl font-bold">{bomDetailData.finished_quantity || 1}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <p className="text-sm text-muted-foreground">物料数</p>
                      <p className="text-2xl font-bold">{bomDetailData.materials?.length || 0}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <p className="text-sm text-muted-foreground">预估成本</p>
                      <p className="text-2xl font-bold">
                        ¥{bomDetailData.estimated_total_cost?.toFixed(2) || '0.00'}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {bomDetailData.process_notes && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-base">
                        <FileText className="h-4 w-4" />
                        工艺备注
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {(() => {
                        try { new URL(bomDetailData.process_notes); return (
                          <a href={bomDetailData.process_notes} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-primary hover:underline">
                            <ExternalLink className="h-4 w-4" />
                            {bomDetailData.process_notes}
                          </a>
                        );} catch { return <p className="text-muted-foreground whitespace-pre-wrap">{bomDetailData.process_notes}</p>; }
                      })()}
                    </CardContent>
                  </Card>
                )}

                {bomDetailData._id && (
                  <FileAttachmentManager
                    entityType="bom"
                    entityId={bomDetailData._id}
                    title="工艺文档附件"
                    maxFiles={20}
                    maxSizeMB={50}
                  />
                )}

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Package className="h-5 w-5" />
                      物料明细
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {bomDetailData.materials?.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        此BOM未定义物料。
                      </div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>物料代码</TableHead>
                            <TableHead>物料名称</TableHead>
                            <TableHead className="text-right">单位用量</TableHead>
                            <TableHead>单位</TableHead>
                            <TableHead className="text-right">损耗率</TableHead>
                            <TableHead className="text-right">预估成本</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {bomDetailData.materials?.map((material, index) => (
                            <TableRow key={material.line_item_id || index}>
                              <TableCell className="font-mono">
                                {material.material_product_code}
                              </TableCell>
                              <TableCell>{material.material_name}</TableCell>
                              <TableCell className="text-right">
                                {material.quantity_per}
                              </TableCell>
                              <TableCell>{material.unit}</TableCell>
                              <TableCell className="text-right">
                                {((material.scrap_rate || 0) * 100).toFixed(1)}%
                              </TableCell>
                              <TableCell className="text-right">
                                {material.estimated_cost
                                  ? `¥${material.estimated_cost.toFixed(2)}`
                                  : '-'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </Card>

                {bomDetailData.notes && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">备注</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground whitespace-pre-wrap">{bomDetailData.notes}</p>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : null}
          </div>
        </SheetContent>
      </Sheet>

      {/* Product Detail Drawer */}
      <Sheet open={productDrawerOpen} onOpenChange={setProductDrawerOpen}>
        <SheetContent side="right" className="w-[600px] max-w-[90vw] overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              产品详情
            </SheetTitle>
            <SheetDescription>
              {selectedProduct?.product_id || selectedProduct?._id}
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-6">
            {productLoading ? (
              <div className="flex justify-center items-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : productError ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{productError}</AlertDescription>
              </Alert>
            ) : selectedProduct ? (
              <div className="space-y-6">
                {/* Basic Info */}
                <Card>
                  <CardHeader>
                    <CardTitle>基本信息</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-muted-foreground">产品编号</p>
                        <p className="font-mono font-medium">{selectedProduct.product_id}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">分类</p>
                        <p>{selectedProduct.category || '-'}</p>
                      </div>
                      <div className="col-span-2">
                        <p className="text-sm text-muted-foreground">名称</p>
                        <p className="font-medium">{selectedProduct.name}</p>
                      </div>
                      <div className="col-span-2">
                        <p className="text-sm text-muted-foreground">规格</p>
                        <p>{selectedProduct.specification || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">物料编码</p>
                        <p className="font-mono">{selectedProduct.client_ref_number || '-'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Manufacturing Parameters */}
                {(selectedProduct.tip_diameter || selectedProduct.total_length || selectedProduct.degree) && (
                  <Card>
                    <CardHeader>
                      <CardTitle>制造参数</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-3 gap-4">
                        {selectedProduct.tip_diameter && (
                          <div>
                            <p className="text-sm text-muted-foreground">刀尖直径</p>
                            <p className="font-medium">{selectedProduct.tip_diameter} mm</p>
                          </div>
                        )}
                        {selectedProduct.total_length && (
                          <div>
                            <p className="text-sm text-muted-foreground">总长度</p>
                            <p className="font-medium">{selectedProduct.total_length} mm</p>
                          </div>
                        )}
                        {selectedProduct.degree && (
                          <div>
                            <p className="text-sm text-muted-foreground">角度</p>
                            <p className="font-medium">{selectedProduct.degree}°</p>
                          </div>
                        )}
                      </div>
                      {selectedProduct.material && (
                        <div>
                          <p className="text-sm text-muted-foreground">材料</p>
                          <p>{selectedProduct.material}</p>
                        </div>
                      )}
                      {selectedProduct.coating && (
                        <div>
                          <p className="text-sm text-muted-foreground">涂层</p>
                          <p>{selectedProduct.coating}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Status */}
                <Card>
                  <CardHeader>
                    <CardTitle>状态信息</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-muted-foreground">状态</p>
                        <p>
                          {selectedProduct.status === 'active' && (
                            <Badge variant="default">有效</Badge>
                          )}
                          {selectedProduct.status === 'inactive' && (
                            <Badge variant="secondary">停用</Badge>
                          )}
                          {!selectedProduct.status && (
                            <Badge variant="outline">未知</Badge>
                          )}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">库存状态</p>
                        <p>{selectedProduct.stock_status || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">单位</p>
                        <p>{selectedProduct.unit || '-'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Product Attachments */}
                {selectedProduct._id && (
                  <FileAttachmentManager
                    entityType="products"
                    entityId={selectedProduct._id}
                    title="产品附件"
                    maxFiles={20}
                    maxSizeMB={50}
                  />
                )}

                {/* Edit Button */}
                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    onClick={() => {
                      handleCloseProductDrawer();
                      navigate(`/products/${selectedProduct._id}/edit`);
                    }}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    编辑产品
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        </SheetContent>
      </Sheet>

      <Dialog open={printDialogOpen} onOpenChange={setPrintDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>打印生产工单</DialogTitle>
            <DialogDescription>
              预览并打印工单详情
            </DialogDescription>
          </DialogHeader>
          
          <div id="print-content" className="py-4">
            <div className="text-center mb-6 border-b pb-4">
              <h1 className="text-2xl font-bold mb-2">{order?.production_order_id}</h1>
              <div className="text-lg text-muted-foreground">
                客户: {order?.client_name || '-'}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
              <div>
                <span className="font-medium">计划开始日期:</span>{' '}
                {order?.planned_start_date ? new Date(order.planned_start_date).toLocaleDateString('zh-CN') : '-'}
              </div>
              <div>
                <span className="font-medium">计划完成日期:</span>{' '}
                {order?.planned_end_date ? new Date(order.planned_end_date).toLocaleDateString('zh-CN') : '-'}
              </div>
            </div>

            <table className="w-full border-collapse border border-gray-300 mb-6">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-4 py-2 text-center w-12">#</th>
                  <th className="border border-gray-300 px-4 py-2 text-left">产品名称</th>
                  <th className="border border-gray-300 px-4 py-2 text-left">产品编号</th>
                  <th className="border border-gray-300 px-4 py-2 text-left">规格</th>
                  <th className="border border-gray-300 px-4 py-2 text-right">数量</th>
                  <th className="border border-gray-300 px-4 py-2 text-left">备注</th>
                </tr>
              </thead>
              <tbody>
                {order?.items?.map((item, index) => (
                  <tr key={index}>
                    <td className="border border-gray-300 px-4 py-2 text-center font-medium">{index + 1}</td>
                    <td className="border border-gray-300 px-4 py-2">{item.finished_product_name}</td>
                    <td className="border border-gray-300 px-4 py-2 font-mono text-sm">{item.finished_product_code}</td>
                    <td className="border border-gray-300 px-4 py-2 text-sm text-gray-600">
                      {item.product_details?.specification || '-'}
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-right">{item.planned_quantity}</td>
                    <td className="border border-gray-300 px-4 py-2"></td>
                  </tr>
                ))}
              </tbody>
            </table>

            {order?.notes && (
              <div className="border-t pt-4">
                <div className="font-medium mb-2">备注:</div>
                <div className="text-sm text-gray-600 whitespace-pre-wrap">{order.notes}</div>
              </div>
            )}
          </div>
          
          <DialogFooter className="print:hidden">
            <Button variant="outline" onClick={() => setPrintDialogOpen(false)}>
              取消
            </Button>
            <Button
              onClick={() => {
                const printContent = document.getElementById('print-content');
                const printWindow = window.open('', '_blank');
                printWindow.document.write(`
                  <html>
                    <head>
                      <title>${order?.production_order_id}</title>
                      <style>
                        body { font-family: Arial, sans-serif; padding: 20px; }
                        h1 { font-size: 24px; margin-bottom: 8px; }
                        .text-center { text-align: center; }
                        .text-muted { color: #666; }
                        .mb-6 { margin-bottom: 24px; }
                        .pb-4 { padding-bottom: 16px; }
                        .border-b { border-bottom: 1px solid #ccc; }
                        table { width: 100%; border-collapse: collapse; margin-bottom: 24px; }
                        th, td { border: 1px solid #ccc; padding: 8px 12px; text-align: left; }
                        th { background-color: #f5f5f5; font-weight: bold; }
                        .text-right { text-align: right; }
                        .text-sm { font-size: 14px; }
                        .whitespace-pre-wrap { white-space: pre-wrap; }
                        @media print {
                          body { padding: 0; }
                        }
                      </style>
                    </head>
                    <body>
                      ${printContent.innerHTML}
                    </body>
                  </html>
                `);
                printWindow.document.close();
                printWindow.focus();
                setTimeout(() => {
                  printWindow.print();
                  printWindow.close();
                }, 250);
              }}
            >
              <Printer className="h-4 w-4 mr-2" />
              打印
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={productSearchOpen} onOpenChange={setProductSearchOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>选择产品</DialogTitle>
            <DialogDescription>搜索并选择要添加到生产工单的产品。</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="按产品编号、名称或规格搜索..."
                value={productSearchTerm}
                onChange={(e) => setProductSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            <div className="max-h-80 overflow-y-auto">
              {productSearchLoading ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : productSearchResults.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground">
                  未找到产品。
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>产品编号</TableHead>
                      <TableHead>名称</TableHead>
                      <TableHead>规格</TableHead>
                      <TableHead>默认BOM</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {productSearchResults.map((product) => (
                      <TableRow
                        key={product._id}
                        className="cursor-pointer hover:bg-muted"
                        onClick={() => handleSelectProduct(product)}
                      >
                        <TableCell className="font-mono">{product.product_id}</TableCell>
                        <TableCell className="font-medium">{product.name}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {product.specification}
                        </TableCell>
                        <TableCell>
                          {product.default_bom_id ? (
                            <Badge variant="success">有</Badge>
                          ) : (
                            <Badge variant="secondary">无</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setProductSearchOpen(false)}>
              取消
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CreateSubcontractDialog
        open={subcontractDialogOpen}
        onOpenChange={setSubcontractDialogOpen}
        productionOrder={order}
        onSuccess={() => {
          loadOrder();
        }}
      />

      {/* Additional Receipt Dialog (部分成品入库) */}
      <Dialog open={additionalReceiptDialogOpen} onOpenChange={handleCloseAdditionalReceiptDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PackagePlus className="h-5 w-5" />
              部分成品入库
            </DialogTitle>
            <DialogDescription>
              为工单 {order?.production_order_id} 入库成品
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Product Items Table */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-base font-medium">成品入库明细</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">默认入库位置:</span>
                  <Input
                    value={additionalReceiptLocation}
                    onChange={(e) => {
                      const newDefault = e.target.value;
                      setAdditionalReceiptLocation(newDefault);
                      setAdditionalReceiptItems(prev =>
                        prev.map(p => p.use_custom_location ? p : { ...p, location: newDefault })
                      );
                    }}
                    placeholder="默认位置"
                    className="h-8 w-32"
                  />
                </div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>产品代码</TableHead>
                    <TableHead>产品名称</TableHead>
                    <TableHead className="text-right">计划数量</TableHead>
                    <TableHead className="text-right">已入库</TableHead>
                    <TableHead className="text-right">入库数量</TableHead>
                    <TableHead className="w-[100px]">自定义位置</TableHead>
                    <TableHead>入库位置</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {additionalReceiptItems.map((item) => (
                    <TableRow key={item.item_index}>
                      <TableCell className="font-mono text-sm">{item.finished_product_code}</TableCell>
                      <TableCell>{item.finished_product_name}</TableCell>
                      <TableCell className="text-right">{item.planned_quantity}</TableCell>
                      <TableCell className="text-right">{item.completed_quantity}</TableCell>
                      <TableCell className="text-right">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.quantity}
                          onChange={(e) => {
                            const value = e.target.value;
                            setAdditionalReceiptItems(prev =>
                              prev.map(it =>
                                it.item_index === item.item_index
                                  ? { ...it, quantity: value }
                                  : it
                              )
                            );
                          }}
                          placeholder="0"
                          className="h-8 w-24 text-right inline-block"
                        />
                      </TableCell>
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={item.use_custom_location}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setAdditionalReceiptItems(prev =>
                              prev.map(it =>
                                it.item_index === item.item_index
                                  ? { ...it, use_custom_location: checked, location: checked ? it.location : additionalReceiptLocation }
                                  : it
                              )
                            );
                          }}
                          className="h-4 w-4"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          value={item.location}
                          onChange={(e) => {
                            const value = e.target.value;
                            setAdditionalReceiptItems(prev =>
                              prev.map(it =>
                                it.item_index === item.item_index
                                  ? { ...it, location: value }
                                  : it
                              )
                            );
                          }}
                          placeholder={additionalReceiptLocation}
                          className="w-32 h-8"
                          disabled={!item.use_custom_location}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <span className="text-sm font-medium">备注</span>
              <Input
                type="text"
                value={additionalReceiptNotes}
                onChange={(e) => setAdditionalReceiptNotes(e.target.value)}
                placeholder="可选：输入入库原因或备注"
                className="h-8"
              />
              <p className="text-xs text-muted-foreground">
                入库将更新成品库存数量，工单状态保持不变。
              </p>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCloseAdditionalReceiptDialog}
              disabled={additionalReceiptLoading}
            >
              取消
            </Button>
            <Button
              onClick={handleSubmitAdditionalReceipt}
              disabled={additionalReceiptLoading}
            >
              {additionalReceiptLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              确认入库
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

export default ProductionDetailPage;
